import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { IMaskInput } from 'react-imask';
import api from '../services/api';
import { McmvRegistrationData, CepResponse } from '../types/McmvRegistration';
import './McmvForm.css';
import { openRegistrationReceipt } from '../utils/printReceipt';

const schema = yup.object().shape({
  // Dados Pessoais
  nome_completo: yup.string().required('Nome completo é obrigatório'),
  nome_social: yup.string().optional().nullable(),
  cpf: yup.string()
    .required('CPF é obrigatório')
    .test('cpf-length', 'CPF deve ter 11 dígitos', (value) => {
      if (!value) return false;
      const cleanCpf = value.replace(/\D/g, '');
      return cleanCpf.length === 11;
    }),
  rg: yup.string().required('RG é obrigatório'),
  data_nascimento: yup.string().required('Data de nascimento é obrigatória'),
  sexo: yup.mixed<'M' | 'F'>().oneOf(['M', 'F'], 'Sexo deve ser M ou F').required('Sexo é obrigatório'),
  orientacao_sexual: yup.mixed<'heterossexual' | 'homossexual' | 'bissexual' | 'pansexual' | 'assexual' | 'outros' | 'nao_informar'>().oneOf(['heterossexual', 'homossexual', 'bissexual', 'pansexual', 'assexual', 'outros', 'nao_informar'], 'Selecione uma orientação sexual válida').required('Orientação sexual é obrigatória'),
  identidade_genero: yup.mixed<'cisgênero' | 'transgênero' | 'não_binário' | 'outros' | 'nao_informar'>().oneOf(['cisgênero', 'transgênero', 'não_binário', 'outros', 'nao_informar'], 'Selecione uma identidade de gênero válida').required('Identidade de gênero é obrigatória'),
  raca_cor: yup.mixed<'branca' | 'preta' | 'parda' | 'amarela' | 'indígena' | 'nao_informar'>().oneOf(['branca', 'preta', 'parda', 'amarela', 'indígena', 'nao_informar'], 'Selecione uma raça/cor válida').required('Raça/cor é obrigatória'),
  estado_nascimento: yup.string().required('Estado de nascimento é obrigatório'),
  estado_civil: yup.mixed<'solteiro' | 'casado' | 'divorciado' | 'viuvo' | 'uniao_estavel'>().oneOf(['solteiro', 'casado', 'divorciado', 'viuvo', 'uniao_estavel'], 'Estado civil inválido').required('Estado civil é obrigatório'),
  escolaridade: yup.mixed<'sem_escolaridade' | 'fundamental_incompleto' | 'fundamental_completo' | 'medio_incompleto' | 'medio_completo' | 'superior_incompleto' | 'superior_completo' | 'pos_graduacao'>().oneOf(['sem_escolaridade', 'fundamental_incompleto', 'fundamental_completo', 'medio_incompleto', 'medio_completo', 'superior_incompleto', 'superior_completo', 'pos_graduacao'], 'Selecione uma escolaridade válida').required('Escolaridade é obrigatória'),
  nome_conjuge: yup.string().optional().nullable(),
  telefone: yup.string()
    .required('Telefone é obrigatório')
    .test('telefone-length', 'Telefone deve ter 10 ou 11 dígitos', (value) => {
      if (!value) return false;
      const cleanTelefone = value.replace(/\D/g, '');
      return cleanTelefone.length >= 10 && cleanTelefone.length <= 11;
    }),
  email: yup.string().email('Email inválido').optional(),
  
  // Endereço
  cep: yup.string()
    .required('CEP é obrigatório')
    .test('cep-length', 'CEP deve ter 8 dígitos', (value) => {
      if (!value) return false;
      const cleanCep = value.replace(/\D/g, '');
      return cleanCep.length === 8;
    }),
  endereco: yup.string().required('Endereço é obrigatório'),
  numero: yup.string().required('Número é obrigatório'),
  complemento: yup.string().optional().nullable(),
  bairro: yup.string().required('Bairro é obrigatório'),
  cidade: yup.string().required('Cidade é obrigatória'),
  estado: yup.string().required('Estado é obrigatório'),
  tempo_residencia_cidade: yup.string().required('Tempo de residência na cidade é obrigatório'),
  
  // Situação Familiar
  numero_dependentes: yup.number()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .required('Número de dependentes é obrigatório')
    .min(0),
  renda_familiar: yup.number()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return undefined;
      }
      return value;
    })
    .required('Renda familiar é obrigatória')
    .min(0, 'Renda familiar deve ser maior ou igual a zero'),
  possui_deficiencia: yup.boolean().required(),
  tipo_deficiencia: yup.string().optional().nullable(),
  
  // Saúde e Condições Especiais
  possui_deficiencia_familiar: yup.boolean().required(),
  tipo_deficiencia_familiar: yup.string().optional().nullable(),
  possui_doenca_cronica: yup.boolean().required(),
  tipo_doenca_cronica: yup.string().optional().nullable(),
  
  // Situação Habitacional
  situacao_habitacional: yup.mixed<'aluguel' | 'casa_propria' | 'casa_cedida' | 'ocupacao_irregular' | 'outros'>().oneOf(['aluguel', 'casa_propria', 'casa_cedida', 'ocupacao_irregular', 'outros'], 'Situação habitacional inválida').required('Situação habitacional é obrigatória'),
  valor_aluguel: yup.number()
    .transform((value, originalValue) => {
      // Se o valor original for uma string vazia, null ou undefined, retorna undefined
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return undefined;
      }
      return value;
    })
    .min(0, 'Valor do aluguel deve ser maior ou igual a zero')
    .optional()
    .nullable(),
  primeira_casa_propria: yup.boolean().required(),
  
  // Dados Profissionais (todos opcionais)
  profissao: yup.string().optional().nullable(),
  empresa: yup.string().optional().nullable(),
  renda_individual: yup.number()
    .transform((value, originalValue) => {
      // Se o valor original for uma string vazia, null ou undefined, retorna undefined
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return undefined;
      }
      return value;
    })
    .min(0, 'Renda individual deve ser maior ou igual a zero')
    .optional()
    .nullable(),
  tipo_renda: yup.mixed<'clt' | 'autonomo' | 'aposentado' | 'pensionista' | 'outros'>()
    .oneOf(['clt', 'autonomo', 'aposentado', 'pensionista', 'outros'], 'Selecione um tipo de renda válido')
    .optional()
    .nullable(),
  
  // Habitação e Participação Social
  situacao_moradia_atual: yup.mixed<'casa_propria' | 'aluguel' | 'cedida' | 'ocupacao' | 'outros'>()
    .oneOf(['casa_propria', 'aluguel', 'cedida', 'ocupacao', 'outros'], 'Selecione uma opção válida para situação da moradia')
    .optional()
    .nullable(),
  tipologia_moradia: yup.mixed<'casa' | 'apartamento' | 'kitnet' | 'comodo' | 'barraco' | 'outros'>()
    .oneOf(['casa', 'apartamento', 'kitnet', 'comodo', 'barraco', 'outros'], 'Selecione uma opção válida para tipologia da moradia')
    .optional()
    .nullable(),
  se_mora_aluguel_valor_30_porcento: yup.mixed<'sim' | 'nao' | 'nao_se_aplica'>()
    .oneOf(['sim', 'nao', 'nao_se_aplica'], 'Selecione: sim, não ou não se aplica')
    .optional()
    .nullable(),
  familia_situacao_rua_trajetoria: yup.mixed<'sim' | 'nao'>()
    .oneOf(['sim', 'nao'], 'Selecione sim ou não')
    .optional()
    .nullable(),
  recebe_auxilio_moradia: yup.mixed<'sim' | 'nao'>()
    .oneOf(['sim', 'nao'], 'Selecione sim ou não')
    .optional()
    .nullable(),
  residencia_local_risco: yup.mixed<'sim' | 'nao'>()
    .oneOf(['sim', 'nao'], 'Selecione sim ou não')
    .optional()
    .nullable(),
  tipo_risco: yup.mixed<'deslizamento' | 'inundacao' | 'contaminacao' | 'multiplos' | 'nao_se_aplica'>()
    .oneOf(['deslizamento', 'inundacao', 'contaminacao', 'multiplos', 'nao_se_aplica'], 'Selecione um tipo de risco válido')
    .optional()
    .nullable(),
  participa_movimento_luta_moradia: yup.mixed<'sim' | 'nao'>()
    .oneOf(['sim', 'nao'], 'Selecione sim ou não')
    .optional()
    .nullable(),
  integrante_povos_indigenas_quilombolas: yup.mixed<'indigena' | 'quilombola' | 'ambos' | 'nao'>()
    .oneOf(['indigena', 'quilombola', 'ambos', 'nao'], 'Selecione uma opção válida')
    .optional()
    .nullable(),
  beneficiario_programa_minha_casa: yup.mixed<'sim_distratado' | 'sim_rescindido' | 'sim_ambos' | 'nao'>()
    .oneOf(['sim_distratado', 'sim_rescindido', 'sim_ambos', 'nao'], 'Selecione uma opção válida')
    .optional()
    .nullable(),
  
  // Observações
  observacoes: yup.string().optional(),

  // Declarações do Edital
  aceite_edital: yup.boolean().oneOf([true], 'Você deve aceitar o edital'),
  termo_veracidade: yup.boolean().oneOf([true], 'Você deve declarar veracidade das informações'),
  termo_lgpd: yup.boolean().oneOf([true], 'Você deve autorizar o tratamento de dados (LGPD)')
}) as yup.ObjectSchema<McmvRegistrationData>;

interface McmvRegistrationFormProps {
  onSuccess?: () => void;
  editData?: {
    id: number;
    user: {
      id: number;
      name: string;
      email: string;
      cpf: string;
    };
    status: 'pendente' | 'aprovado' | 'reprovado' | 'em_analise';
    created_at: string;
    updated_at: string;
    // Adicionar todos os campos do formulário
    nome_completo?: string;
    nome_social?: string;
    cpf?: string;
    rg?: string;
    data_nascimento?: string;
    sexo?: 'M' | 'F';
    orientacao_sexual?: string;
    identidade_genero?: string;
    raca_cor?: string;
    estado_nascimento?: string;
    estado_civil?: string;
    escolaridade?: string;
    nome_conjuge?: string;
    telefone?: string;
    email?: string;
    cep?: string;
    endereco?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    tempo_residencia_cidade?: string;
    numero_dependentes?: number;
    renda_familiar?: number;
    possui_deficiencia?: boolean;
    tipo_deficiencia?: string;
    possui_deficiencia_familiar?: boolean;
    tipo_deficiencia_familiar?: string;
    possui_doenca_cronica?: boolean;
    tipo_doenca_cronica?: string;
    situacao_habitacional?: string;
    valor_aluguel?: number;
    primeira_casa_propria?: boolean;
    profissao?: string;
    empresa?: string;
    renda_individual?: number;
    tipo_renda?: string;
    situacao_moradia_atual?: string;
    tipologia_moradia?: string;
    se_mora_aluguel_valor_30_porcento?: string;
    familia_situacao_rua_trajetoria?: string;
    recebe_auxilio_moradia?: string;
    residencia_local_risco?: string;
    tipo_risco?: string;
    participa_movimento_luta_moradia?: string;
    integrante_povos_indigenas_quilombolas?: string;
    beneficiario_programa_minha_casa?: string;
    observacoes?: string;
  } | null;
}

const McmvRegistrationForm: React.FC<McmvRegistrationFormProps> = ({ onSuccess, editData }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [numeroInscricao, setNumeroInscricao] = useState<string | null>(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    control,
    reset
  } = useForm<McmvRegistrationData>({
    resolver: yupResolver(schema),
    defaultValues: {
      possui_deficiencia: false,
      possui_deficiencia_familiar: false,
      possui_doenca_cronica: false,
      primeira_casa_propria: true,
      numero_dependentes: 0,
      // Declarações (inicialmente desmarcadas)
      aceite_edital: false,
      termo_veracidade: false,
      termo_lgpd: false
    }
  });

  // Carregar dados existentes do usuário ou dados para edição
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingData(true);
        
        if (editData) {
          // Modo edição - carregar dados da inscrição específica
          const response = await api.get(`/admin/registrations/${editData.id}`);
          const data = response.data.data; // A API retorna { message, data }
          
          // Formatar dados para o formulário
          const formattedData = {
            ...data,
            cpf: data.cpf ? data.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '',
            telefone: data.telefone ? data.telefone.replace(/(\d{2})(\d{4,5})(\d{4})/, '($1) $2-$3') : '',
            cep: data.cep ? data.cep.replace(/(\d{5})(\d{3})/, '$1-$2') : '',
            data_nascimento: data.data_nascimento ? data.data_nascimento.split('T')[0] : ''
          };
          
          console.log('Dados carregados para edição:', formattedData); // Para debug
          reset(formattedData);
        } else {
          // Modo normal - carregar dados do usuário logado
          const response = await api.get('/mcmv/registration');
          
          if (response.data.data) {
            const data = response.data.data;
            
            const formattedData = {
              ...data,
              cpf: data.cpf ? data.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '',
              telefone: data.telefone ? data.telefone.replace(/(\d{2})(\d{4,5})(\d{4})/, '($1) $2-$3') : '',
              cep: data.cep ? data.cep.replace(/(\d{5})(\d{3})/, '$1-$2') : '',
              data_nascimento: data.data_nascimento ? data.data_nascimento.split('T')[0] : ''
            };
            
            reset(formattedData);
          }
        }
      } catch (error: any) {
        console.error('Erro ao carregar dados:', error);
        if (error.response?.status !== 404) {
          alert('Erro ao carregar dados da inscrição. Tente novamente.');
        }
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [reset, editData]);

  const watchEstadoCivil = watch('estado_civil');
  const watchPossuiDeficiencia = watch('possui_deficiencia');
  const watchPossuiDeficienciaFamiliar = watch('possui_deficiencia_familiar');
  const watchPossuiDoencaCronica = watch('possui_doenca_cronica');
  const watchSituacaoHabitacional = watch('situacao_habitacional');

  const searchCep = async (cep: string) => {
    if (cep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data: CepResponse = await response.json();
        
        if (!data.erro) {
          setValue('endereco', data.logradouro);
          setValue('bairro', data.bairro);
          setValue('cidade', data.localidade);
          setValue('estado', data.uf);
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  const nextStep = async () => {
    const fieldsToValidate = getFieldsForStep(currentStep);
    const isValid = await trigger(fieldsToValidate);
    
    if (isValid) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const getFieldsForStep = (step: number): (keyof McmvRegistrationData)[] => {
    switch (step) {
      case 1:
        return ['nome_completo', 'cpf', 'rg', 'data_nascimento', 'sexo', 'orientacao_sexual', 'identidade_genero', 'raca_cor', 'estado_nascimento', 'estado_civil', 'escolaridade', 'telefone', 'email'];
      case 2:
        return ['cep', 'endereco', 'numero', 'bairro', 'cidade', 'estado', 'tempo_residencia_cidade'];
      case 3:
        return ['numero_dependentes', 'renda_familiar', 'possui_deficiencia'];
      case 4:
        return ['possui_deficiencia_familiar', 'possui_doenca_cronica'];
      case 5:
        return ['situacao_habitacional', 'primeira_casa_propria'];
      case 6:
        return []; // Etapa de dados profissionais - todos opcionais
      case 7:
        // Tornar obrigatórias as declarações do edital
        return ['aceite_edital', 'termo_veracidade', 'termo_lgpd'];

      default:
        return [];
    }
  };

  // Adicione esta função ANTES da função onSubmit (linha 214):
  const onError = (errors: any) => {
    console.log('❌ Erros de validação:', errors);
    console.log('📋 Campos com erro:', Object.keys(errors));
    
    // Mostrar detalhes dos erros
    const errorMessages = Object.entries(errors).map(([field, error]: [string, any]) => {
      return `${field}: ${error.message}`;
    }).join('\n');
    
    alert(`Erro de validação!\n\nCampos com problema:\n${errorMessages}`);
  };

  const onSubmit = async (data: McmvRegistrationData) => {
    try {
      setIsLoading(true);
      
      // Limpar formatação dos campos
      const cleanData = {
        ...data,
        cpf: data.cpf.replace(/\D/g, ''),
        telefone: data.telefone.replace(/\D/g, ''),
        cep: data.cep.replace(/\D/g, '')
      };

      let response;
      if (editData) {
        // Modo edição
        response = await api.put(`/admin/registrations/${editData.id}`, cleanData);
      } else {
        // Modo criação
        response = await api.post('/mcmv/registrations', cleanData);
      }

      // Capturar o número de inscrição da resposta
      if (response.data.numero_inscricao || response.data.data?.numero_inscricao) {
        setNumeroInscricao(response.data.numero_inscricao || response.data.data.numero_inscricao);
      }
      
      setSubmitSuccess(true);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar os dados. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Mostrar loading enquanto carrega os dados
  if (isLoadingData) {
    return (
      <div className="mcmv-form-container">
        <div className="form-header">
          <h1>Carregando dados...</h1>
          <p>Aguarde enquanto carregamos suas informações.</p>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="mcmv-form-container">
        <div className="success-message">
          <h2>✅ Cadastro realizado com sucesso!</h2>
          {numeroInscricao && (
            <div className="numero-inscricao">
              <h3>Número da Inscrição:</h3>
              <p className="numero-destaque">{numeroInscricao}</p>
            </div>
          )}
          <p>Seu cadastro foi enviado e está sendo analisado.</p>

          <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
            <button
              onClick={() => {
                const nome = watch('nome_completo') || '';
                const cpf = watch('cpf') || '';
                const email = watch('email') || '';
                if (!numeroInscricao) {
                  alert('Número de inscrição não encontrado.');
                  return;
                }
                openRegistrationReceipt({
                  numero_inscricao: numeroInscricao,
                  nome,
                  cpf,
                  email,
                  created_at: new Date().toISOString(),
                  status: 'Recebida',
                });
              }}
              className="btn-secondary"
            >
              Imprimir Comprovante
            </button>

            <button 
              onClick={() => {
                setSubmitSuccess(false);
                setCurrentStep(1);
                setNumeroInscricao(null);
              }}
              className="btn-primary"
            >
              Fazer novo cadastro
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mcmv-form-container">
      <div className="form-header">
        <h1>{editData ? 'Editar Cadastro MCMV' : 'Cadastro MCMV - Minha Casa Minha Vida'}</h1>
        <div className="progress-bar">
          <div className="progress" style={{ width: `${(currentStep / 7) * 100}%` }}></div>
        </div>
        <p>Etapa {currentStep} de 7</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onError)} className="mcmv-form">
        {/* Etapa 1: Dados Pessoais */}
        {currentStep === 1 && (
          <div className="form-step">
            <h2>Dados Pessoais</h2>
            
            {/* CPF - Campo único */}
            <div className="form-group">
              <label>CPF *</label>
              <Controller
                name="cpf"
                control={control}
                render={({ field }) => (
                  <IMaskInput
                    mask="000.000.000-00"
                    value={field.value || ''}
                    onAccept={(value) => field.onChange(value)}
                    placeholder="000.000.000-00"
                    className={errors.cpf ? 'error' : ''}
                  />
                )}
              />
              {errors.cpf && <span className="error-message">{errors.cpf.message}</span>}
            </div>

            {/* NOME COMPLETO - Campo único */}
            <div className="form-group">
              <label>Nome Completo *</label>
              <input
                type="text"
                {...register('nome_completo')}
                className={errors.nome_completo ? 'error' : ''}
                placeholder="Nome completo"
              />
              {errors.nome_completo && <span className="error-message">{errors.nome_completo.message}</span>}
            </div>

            <div className="form-group">
              <label>Número NIS *</label>
              <input
                type="text"
                {...register('nome_completo')}
                className={errors.nome_completo ? 'error' : ''}
                placeholder="Nome completo"
              />
              {errors.nome_completo && <span className="error-message">{errors.nome_completo.message}</span>}
            </div>

            {/* NOME SOCIAL - Campo único */}
            <div className="form-group">
              <label>Nome Social (se houver)</label>
              <input
                type="text"
                {...register('nome_social')}
                placeholder="Nome pelo qual prefere ser chamado(a)"
              />
            </div>

            {/* DATA DE NASCIMENTO e SEXO */}
            <div className="form-row">
              <div className="form-group">
                <label>Data de Nascimento *</label>
                <input
                  type="date"
                  {...register('data_nascimento')}
                  className={errors.data_nascimento ? 'error' : ''}
                />
                {errors.data_nascimento && <span className="error-message">{errors.data_nascimento.message}</span>}
              </div>

              <div className="form-group">
                <label>Sexo *</label>
                <select {...register('sexo')} className={errors.sexo ? 'error' : ''}>
                  <option value="">Selecione</option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                </select>
                {errors.sexo && <span className="error-message">{errors.sexo.message}</span>}
              </div>
            </div>

            {/* ORIENTAÇÃO SEXUAL e IDENTIDADE DE GÊNERO */}
            <div className="form-row">
              <div className="form-group">
                <label>Orientação Sexual *</label>
                <select {...register('orientacao_sexual')} className={errors.orientacao_sexual ? 'error' : ''}>
                  <option value="">Selecione</option>
                  <option value="heterossexual">Heterossexual</option>
                  <option value="homossexual">Homossexual</option>
                  <option value="bissexual">Bissexual</option>
                  <option value="pansexual">Pansexual</option>
                  <option value="assexual">Assexual</option>
                  <option value="outros">Outros</option>
                  <option value="nao_informar">Prefiro não informar</option>
                </select>
                {errors.orientacao_sexual && <span className="error-message">{errors.orientacao_sexual.message}</span>}
              </div>

              <div className="form-group">
                <label>Identidade de Gênero *</label>
                <select {...register('identidade_genero')} className={errors.identidade_genero ? 'error' : ''}>
                  <option value="">Selecione</option>
                  <option value="cisgênero">Cisgênero</option>
                  <option value="transgênero">Transgênero</option>
                  <option value="não_binário">Não-binário</option>
                  <option value="outros">Outros</option>
                  <option value="nao_informar">Prefiro não informar</option>
                </select>
                {errors.identidade_genero && <span className="error-message">{errors.identidade_genero.message}</span>}
              </div>
            </div>

            {/* RAÇA/COR e ESTADO ONDE NASCEU */}
            <div className="form-row">
              <div className="form-group">
                <label>Raça/Cor *</label>
                <select {...register('raca_cor')} className={errors.raca_cor ? 'error' : ''}>
                  <option value="">Selecione</option>
                  <option value="branca">Branca</option>
                  <option value="preta">Preta</option>
                  <option value="parda">Parda</option>
                  <option value="amarela">Amarela</option>
                  <option value="indígena">Indígena</option>
                  <option value="nao_informar">Prefiro não informar</option>
                </select>
                {errors.raca_cor && <span className="error-message">{errors.raca_cor.message}</span>}
              </div>

              <div className="form-group">
                <label>Estado onde Nasceu *</label>
                <select {...register('estado_nascimento')} className={errors.estado_nascimento ? 'error' : ''}>
                  <option value="">Selecione</option>
                  <option value="AC">Acre</option>
                  <option value="AL">Alagoas</option>
                  <option value="AP">Amapá</option>
                  <option value="AM">Amazonas</option>
                  <option value="BA">Bahia</option>
                  <option value="CE">Ceará</option>
                  <option value="DF">Distrito Federal</option>
                  <option value="ES">Espírito Santo</option>
                  <option value="GO">Goiás</option>
                  <option value="MA">Maranhão</option>
                  <option value="MT">Mato Grosso</option>
                  <option value="MS">Mato Grosso do Sul</option>
                  <option value="MG">Minas Gerais</option>
                  <option value="PA">Pará</option>
                  <option value="PB">Paraíba</option>
                  <option value="PR">Paraná</option>
                  <option value="PE">Pernambuco</option>
                  <option value="PI">Piauí</option>
                  <option value="RJ">Rio de Janeiro</option>
                  <option value="RN">Rio Grande do Norte</option>
                  <option value="RS">Rio Grande do Sul</option>
                  <option value="RO">Rondônia</option>
                  <option value="RR">Roraima</option>
                  <option value="SC">Santa Catarina</option>
                  <option value="SP">São Paulo</option>
                  <option value="SE">Sergipe</option>
                  <option value="TO">Tocantins</option>
                </select>
                {errors.estado_nascimento && <span className="error-message">{errors.estado_nascimento.message}</span>}
              </div>
            </div>

            {/* ESTADO CIVIL e ESCOLARIDADE */}
            <div className="form-row">
              <div className="form-group">
                <label>Estado Civil *</label>
                <select {...register('estado_civil')} className={errors.estado_civil ? 'error' : ''}>
                  <option value="">Selecione</option>
                  <option value="solteiro">Solteiro(a)</option>
                  <option value="casado">Casado(a)</option>
                  <option value="uniao_estavel">União Estável</option>
                  <option value="divorciado">Divorciado(a)</option>
                  <option value="viuvo">Viúvo(a)</option>
                </select>
                {errors.estado_civil && <span className="error-message">{errors.estado_civil.message}</span>}
              </div>

              <div className="form-group">
                <label>Escolaridade *</label>
                <select {...register('escolaridade')} className={errors.escolaridade ? 'error' : ''}>
                  <option value="">Selecione</option>
                  <option value="sem_escolaridade">Sem escolaridade</option>
                  <option value="fundamental_incompleto">Ensino Fundamental Incompleto</option>
                  <option value="fundamental_completo">Ensino Fundamental Completo</option>
                  <option value="medio_incompleto">Ensino Médio Incompleto</option>
                  <option value="medio_completo">Ensino Médio Completo</option>
                  <option value="superior_incompleto">Ensino Superior Incompleto</option>
                  <option value="superior_completo">Ensino Superior Completo</option>
                  <option value="pos_graduacao">Pós-graduação</option>
                </select>
                {errors.escolaridade && <span className="error-message">{errors.escolaridade.message}</span>}
              </div>
            </div>

            {/* NOME DO CÔNJUGE - Condicional - Campo único */}
            {(watchEstadoCivil === 'casado' || watchEstadoCivil === 'uniao_estavel') && (
              <div className="form-group">
                <label>Nome do Cônjuge</label>
                <input
                  type="text"
                  {...register('nome_conjuge')}
                  placeholder="Nome completo do cônjuge"
                />
              </div>
            )}

            {/* RG e TELEFONE */}
            <div className="form-row">
              <div className="form-group">
                <label>RG *</label>
                <input
                  type="text"
                  {...register('rg')}
                  className={errors.rg ? 'error' : ''}
                  placeholder="Número do RG"
                />
                {errors.rg && <span className="error-message">{errors.rg.message}</span>}
              </div>

              <div className="form-group">
                <label>Telefone *</label>
                <Controller
                  name="telefone"
                  control={control}
                  render={({ field }) => (
                    <IMaskInput
                      mask="(00) 00000-0000"
                      value={field.value || ''}
                      onAccept={(value) => field.onChange(value)}
                      placeholder="(00) 00000-0000"
                      className={errors.telefone ? 'error' : ''}
                    />
                  )}
                />
                {errors.telefone && <span className="error-message">{errors.telefone.message}</span>}
              </div>
            </div>

            {/* EMAIL - Campo único */}
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                {...register('email')}
                className={errors.email ? 'error' : ''}
                placeholder="seu@email.com"
              />
              {errors.email && <span className="error-message">{errors.email.message}</span>}
            </div>
          </div>
        )}



        {/* Etapa 2: Endereço */}
        {currentStep === 2 && (
          <div className="form-step">
            <h2>Endereço</h2>
            
            <div className="form-group">
              <label>CEP *</label>
              <Controller
                name="cep"
                control={control}
                render={({ field }) => (
                  <IMaskInput
                    mask="00000-000"
                    value={field.value || ''}
                    onAccept={(value) => {
                      field.onChange(value);
                      const cep = value.replace(/\D/g, '');
                      if (cep.length === 8) {
                        searchCep(cep);
                      }
                    }}
                    className={errors.cep ? 'error' : ''}
                  />
                )}
              />
              {errors.cep && <span className="error-message">{errors.cep.message}</span>}
            </div>

            <div className="form-row">
              <div className="form-group flex-2">
                <label>Endereço *</label>
                <input
                  type="text"
                  {...register('endereco')}
                  className={errors.endereco ? 'error' : ''}
                />
                {errors.endereco && <span className="error-message">{errors.endereco.message}</span>}
              </div>

              <div className="form-group">
                <label>Número *</label>
                <input
                  type="text"
                  {...register('numero')}
                  className={errors.numero ? 'error' : ''}
                />
                {errors.numero && <span className="error-message">{errors.numero.message}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Complemento</label>
              <input
                type="text"
                {...register('complemento')}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Bairro *</label>
                <input
                  type="text"
                  {...register('bairro')}
                  className={errors.bairro ? 'error' : ''}
                />
                {errors.bairro && <span className="error-message">{errors.bairro.message}</span>}
              </div>

              <div className="form-group">
                <label>Cidade *</label>
                <input
                  type="text"
                  {...register('cidade')}
                  className={errors.cidade ? 'error' : ''}
                />
                {errors.cidade && <span className="error-message">{errors.cidade.message}</span>}
              </div>

              <div className="form-group">
                <label>Estado *</label>
                <input
                  type="text"
                  {...register('estado')}
                  className={errors.estado ? 'error' : ''}
                  maxLength={2}
                />
                {errors.estado && <span className="error-message">{errors.estado.message}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Mora desde quanto nesta cidade? *</label>
              <select {...register('tempo_residencia_cidade')} className={errors.tempo_residencia_cidade ? 'error' : ''}>
                <option value="">Selecione</option>
                <option value="menos_1_ano">Menos de 1 ano</option>
                <option value="1_2_anos">1 a 2 anos</option>
                <option value="3_5_anos">3 a 5 anos</option>
                <option value="6_10_anos">6 a 10 anos</option>
                <option value="11_20_anos">11 a 20 anos</option>
                <option value="mais_20_anos">Mais de 20 anos</option>
                <option value="desde_nascimento">Desde o nascimento</option>
              </select>
              {errors.tempo_residencia_cidade && <span className="error-message">{errors.tempo_residencia_cidade.message}</span>}
            </div>
          </div>
        )}

        {/* Etapa 3: Situação Familiar */}
        {currentStep === 3 && (
          <div className="form-step">
            <h2>Situação Familiar</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Número de Dependentes *</label>
                <input
                  type="number"
                  min="0"
                  {...register('numero_dependentes')}
                  className={errors.numero_dependentes ? 'error' : ''}
                />
                {errors.numero_dependentes && <span className="error-message">{errors.numero_dependentes.message}</span>}
              </div>

              <div className="form-group">
                <label>Renda Familiar (R$) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('renda_familiar')}
                  className={errors.renda_familiar ? 'error' : ''}
                />
                {errors.renda_familiar && <span className="error-message">{errors.renda_familiar.message}</span>}
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  {...register('possui_deficiencia')}
                />
                Possui alguma deficiência?
              </label>
            </div>

            {watchPossuiDeficiencia && (
              <div className="form-group">
                <label>Tipo de Deficiência</label>
                <textarea
                  {...register('tipo_deficiencia')}
                  rows={3}
                  placeholder="Descreva o tipo de deficiência"
                />
              </div>
            )}
          </div>
        )}

        {/* Etapa 4: Saúde e Condições Especiais */}
        {currentStep === 4 && (
          <div className="form-step">
            <h2>Saúde e Condições Especiais</h2>
            
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  {...register('possui_deficiencia_familiar')}
                />
                Algum familiar possui deficiência?
              </label>
            </div>

            {watchPossuiDeficienciaFamiliar && (
              <div className="form-group">
                <label>Tipo de Deficiência do Familiar</label>
                <textarea
                  {...register('tipo_deficiencia_familiar')}
                  rows={3}
                  placeholder="Descreva o tipo de deficiência do familiar"
                />
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  {...register('possui_doenca_cronica')}
                />
                Possui alguma doença crônica?
              </label>
            </div>

            {watchPossuiDoencaCronica && (
              <div className="form-group">
                <label>Tipo de Doença Crônica</label>
                <textarea
                  {...register('tipo_doenca_cronica')}
                  rows={3}
                  placeholder="Descreva o tipo de doença crônica"
                />
              </div>
            )}
          </div>
        )}

        {/* Etapa 5: Situação Habitacional */}
        {currentStep === 5 && (
          <div className="form-step">
            <h2>Situação Habitacional</h2>
            
            <div className="form-group">
              <label>Situação Habitacional Atual *</label>
              <select {...register('situacao_habitacional')} className={errors.situacao_habitacional ? 'error' : ''}>
                <option value="">Selecione</option>
                <option value="aluguel">Aluguel</option>
                <option value="casa_propria">Casa Própria</option>
                <option value="casa_cedida">Casa Cedida</option>
                <option value="ocupacao_irregular">Ocupação Irregular</option>
                <option value="outros">Outros</option>
              </select>
              {errors.situacao_habitacional && <span className="error-message">{errors.situacao_habitacional.message}</span>}
            </div>

            {watchSituacaoHabitacional === 'aluguel' && (
              <div className="form-group">
                <label>Valor do Aluguel (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('valor_aluguel')}
                />
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  {...register('primeira_casa_propria')}
                />
                Esta será sua primeira casa própria?
              </label>
            </div>
          </div>
        )}

        {/* Etapa 6: Dados Profissionais */}
        {currentStep === 6 && (
          <div className="form-step">
            <h2>Dados Profissionais (Opcional)</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Profissão</label>
                <input
                  type="text"
                  {...register('profissao')}
                />
              </div>

              <div className="form-group">
                <label>Empresa</label>
                <input
                  type="text"
                  {...register('empresa')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Renda Individual (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('renda_individual')}
                />
              </div>

              <div className="form-group">
                <label>Tipo de Renda</label>
                <select {...register('tipo_renda')}>
                  <option value="">Selecione</option>
                  <option value="clt">CLT</option>
                  <option value="autonomo">Autônomo</option>
                  <option value="aposentado">Aposentado</option>
                  <option value="pensionista">Pensionista</option>
                  <option value="outros">Outros</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Etapa 7: Habitação e Participação Social */}
        {currentStep === 7 && (
          <div className="form-step">
            <h2>Habitação e Participação Social</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Situação da Moradia Atual</label>
                <select {...register('situacao_moradia_atual')}>
                  <option value="">Selecione</option>
                  <option value="casa_propria">Casa Própria</option>
                  <option value="aluguel">Aluguel</option>
                  <option value="cedida">Cedida</option>
                  <option value="ocupacao">Ocupação</option>
                  <option value="outros">Outros</option>
                </select>
              </div>

              <div className="form-group">
                <label>Tipologia da Moradia</label>
                <select {...register('tipologia_moradia')}>
                  <option value="">Selecione</option>
                  <option value="casa">Casa</option>
                  <option value="apartamento">Apartamento</option>
                  <option value="kitnet">Kitnet</option>
                  <option value="comodo">Cômodo</option>
                  <option value="barraco">Barraco</option>
                  <option value="outros">Outros</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Se mora de aluguel, o valor corresponde a mais de 30% da renda familiar?</label>
              <select {...register('se_mora_aluguel_valor_30_porcento')}>
                <option value="">Selecione</option>
                <option value="sim">Sim</option>
                <option value="nao">Não</option>
                <option value="nao_se_aplica">Não se aplica</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>A família tem situação de rua ou trajetória de rua?</label>
                <select {...register('familia_situacao_rua_trajetoria')}>
                  <option value="">Selecione</option>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </div>

              <div className="form-group">
                <label>Recebe auxílio moradia do poder público?</label>
                <select {...register('recebe_auxilio_moradia')}>
                  <option value="">Selecione</option>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>A residência está localizada em área de risco?</label>
                <select {...register('residencia_local_risco')}>
                  <option value="">Selecione</option>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </div>

              <div className="form-group">
                <label>Tipo de Risco</label>
                <select {...register('tipo_risco')}>
                  <option value="">Selecione</option>
                  <option value="deslizamento">Deslizamento</option>
                  <option value="inundacao">Inundação</option>
                  <option value="contaminacao">Contaminação</option>
                  <option value="multiplos">Múltiplos</option>
                  <option value="nao_se_aplica">Não se aplica</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Participa de movimento de luta por moradia?</label>
              <select {...register('participa_movimento_luta_moradia')}>
                <option value="">Selecione</option>
                <option value="sim">Sim</option>
                <option value="nao">Não</option>
              </select>
            </div>

            <div className="form-group">
              <label>É integrante de povos indígenas ou quilombolas?</label>
              <select {...register('integrante_povos_indigenas_quilombolas')}>
                <option value="">Selecione</option>
                <option value="indigena">Indígena</option>
                <option value="quilombola">Quilombola</option>
                <option value="ambos">Ambos</option>
                <option value="nao">Não</option>
              </select>
            </div>

            <div className="form-group">
              <label>Já foi beneficiário do programa Minha Casa Minha Vida?</label>
              <select {...register('beneficiario_programa_minha_casa')}>
                <option value="">Selecione</option>
                <option value="sim_distratado">Sim - Distratado</option>
                <option value="sim_rescindido">Sim - Rescindido</option>
                <option value="sim_ambos">Sim - Ambos</option>
                <option value="nao">Não</option>
              </select>
            </div>

            <div className="form-group">
              <label>Observações</label>
              <textarea
                {...register('observacoes')}
                rows={4}
                placeholder="Informações adicionais que julgar importantes"
              />
            </div>

            {/* Declarações do Edital */}
            <div className="form-group">
              <p>
                Leia o edital completo antes de finalizar:&nbsp;
                <a href="/files/edital-jf-mcmv.pdf" target="_blank" rel="noopener noreferrer">
                  Edital do Programa MCMV (PDF)
                </a>
              </p>

              <label className="checkbox-label">
                <input type="checkbox" {...register('aceite_edital')} />
                Li o edital e aceito suas condições.
              </label>
              {errors.aceite_edital && <span className="error-message">{errors.aceite_edital.message}</span>}

              <label className="checkbox-label">
                <input type="checkbox" {...register('termo_veracidade')} />
                Declaro, sob as penas da lei, que todas as informações prestadas são verdadeiras.
              </label>
              {errors.termo_veracidade && <span className="error-message">{errors.termo_veracidade.message}</span>}

              <label className="checkbox-label">
                <input type="checkbox" {...register('termo_lgpd')} />
                Autorizo o tratamento de meus dados pessoais conforme a LGPD.
              </label>
              {errors.termo_lgpd && <span className="error-message">{errors.termo_lgpd.message}</span>}
            </div>
          </div>
        )}

        {/* Botões de navegação */}
        <div className="form-navigation">
          {currentStep > 1 && (
            <button type="button" onClick={prevStep} className="btn btn-secondary">
              Anterior
            </button>
          )}
          
          {currentStep < 7 ? (
            <button type="button" onClick={nextStep} className="btn btn-primary">
              Próximo
            </button>
          ) : (
            <button type="submit" disabled={isLoading} className={`btn btn-primary ${isLoading ? 'btn-loading' : ''}`}>
              {isLoading ? 'Enviando...' : 'Finalizar Cadastro'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default McmvRegistrationForm;